<template>
  <BaseExtractedDetails 
    :sections="sections" 
    :has-data="hasData"
    :data="data"
  />
</template>

<script>
import BaseExtractedDetails from './BaseExtractedDetails.vue'

export default {
  name: 'AECBReportDetails',
  components: {
    BaseExtractedDetails
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    hasData() {
      if (!this.data) return false;
      
      // Handle array data structure
      if (Array.isArray(this.data)) {
        return this.data.length > 0 && this.data[0] && Object.keys(this.data[0]).length > 0;
      }
      
      // Handle object data structure
      return Object.keys(this.data).length > 0;
    },
    
    sections() {
      if (!this.data) return [];
      
      // Get the first item from the data array (AECB report data)
      const reportData = Array.isArray(this.data) ? this.data[0] : this.data;
      if (!reportData) return [];
      
      return [
        // Company Information
        {
          title: 'Company Information',
          icon: 'las la-building',
          type: 'info-grid',
          fields: [
            {
              key: 'company_info.name',
              label: 'Company Name',
              format: 'text'
            },
            {
              key: 'company_info.trade_license_number',
              label: 'Trade License Number',
              format: 'text'
            },
            {
              key: 'company_info.trade_license_place',
              label: 'Trade License Place',
              format: 'text'
            },
            {
              key: 'company_info.registration_date',
              label: 'Registration Date',
              format: 'date'
            },
            {
              key: 'company_info.economic_activity',
              label: 'Economic Activity',
              format: 'text'
            }
          ]
        },
        
        // Contact Details
        {
          title: 'Contact Details',
          icon: 'las la-phone',
          type: 'info-grid',
          fields: [
            {
              key: 'contact_details.email',
              label: 'Email',
              format: 'text'
            },
            {
              key: 'contact_details.phone',
              label: 'Phone',
              format: 'text'
            },
            {
              key: 'contact_details.address',
              label: 'Address',
              format: 'text'
            }
          ]
        },
        {
          title: 'Overview of Credit Facilities',
          icon: 'las la-credit-card',
          type: 'info-grid',
          fields: [
            {
              key: 'overview_of_credit_facilities.no_of_active_credit_facilities',
              label: 'No of Active Credit Facilities',
              format: 'text'
            },
            {
              key: 'overview_of_credit_facilities.total_outstanding_balance_amount',
              label: 'Total Outstanding Balance Amount',
              format: 'currency'
            },
            {
              key: 'overview_of_credit_facilities.total_overdue_amount',
              label: 'Total Overdue Amount',
              format: 'currency'
            },
            {
              key: 'overview_of_credit_facilities.total_payment_amount',
              label: 'Total Payment Amount',
              format: 'currency'
            }
          ],
          data: reportData.overview_of_credit_facilities
        },

        // Active Credit Facilities
        ...(reportData.credit_facility_details && reportData.credit_facility_details.active && reportData.credit_facility_details.active.length > 0 ? [{
          title: 'Active Credit Facilities',
          icon: 'las la-credit-card',
          type: 'credit-facilities',
          fields: [
            {
              key: 'dp_contract_no',
              label: 'Contract No',
              format: 'text'
            },
            {
              key: 'provider',
              label: 'Provider',
              format: 'text'
            },
            {
              key: 'outstanding_balance',
              label: 'Outstanding Balance',
              format: 'currency'
            },
            {
              key: 'total_amount',
              label: 'Total Amount',
              format: 'currency'
            },
            {
              key: 'payment_amount',
              label: 'Payment Amount',
              format: 'currency'
            },
            {
              key: 'start_date',
              label: 'Start Date',
              format: 'date'
            },
            {
              key: 'closed_date',
              label: 'Closed Date',
              format: 'date'
            },
            {
              key: 'total_no_of_instalments',
              label: 'Total Instalments',
              format: 'text'
            },
            {
              key: 'no_of_remaining_instalments',
              label: 'Remaining Instalments',
              format: 'text'
            },
            {
              key: 'payments_frequency',
              label: 'Payment Frequency',
              format: 'text'
            },
            {
              key: 'overdue_amount',
              label: 'Overdue Amount',
              format: 'currency'
            },
            {
              key: 'islamic_contract_flag',
              label: 'Islamic Contract',
              format: 'text'
            },
            {
              key: 'secured_contract_flag',
              label: 'Secured Contract',
              format: 'text'
            },
            {
              key: 'security_type',
              label: 'Security Type',
              format: 'text'
            },
            {
              key: 'security_code_description',
              label: 'Security Description',
              format: 'text'
            },
            {
              key: 'worst_status',
              label: 'Worst Status',
              format: 'text'
            },
            {
              key: 'worst_status_date',
              label: 'Worst Status Date',
              format: 'date'
            },
            {
              key: 'notes',
              label: 'Notes',
              format: 'text'
            }
          ],
          data: reportData.credit_facility_details.active
        }] : []),
        // Closed Credit Facilities
        ...(reportData.credit_facility_details && reportData.credit_facility_details.closed && reportData.credit_facility_details.closed.length > 0 ? [{
          title: 'Closed Credit Facilities',
          icon: 'las la-history',
          type: 'credit-facilities',
          fields: [
            {
              key: 'dp_contract_no',
              label: 'Contract No',
              format: 'text'
            },
            {
              key: 'provider',
              label: 'Provider',
              format: 'text'
            },
            {
              key: 'outstanding_balance',
              label: 'Outstanding Balance',
              format: 'currency'
            },
            {
              key: 'total_amount',
              label: 'Total Amount',
              format: 'currency'
            },
            {
              key: 'payment_amount',
              label: 'Payment Amount',
              format: 'currency'
            },
            {
              key: 'start_date',
              label: 'Start Date',
              format: 'date'
            },
            {
              key: 'closed_date',
              label: 'Closed Date',
              format: 'date'
            },
            {
              key: 'total_no_of_instalments',
              label: 'Total Instalments',
              format: 'text'
            },
            {
              key: 'no_of_remaining_instalments',
              label: 'Remaining Instalments',
              format: 'text'
            },
            {
              key: 'payments_frequency',
              label: 'Payment Frequency',
              format: 'text'
            },
            {
              key: 'overdue_amount',
              label: 'Overdue Amount',
              format: 'currency'
            },
            {
              key: 'islamic_contract_flag',
              label: 'Islamic Contract',
              format: 'text'
            },
            {
              key: 'secured_contract_flag',
              label: 'Secured Contract',
              format: 'text'
            },
            {
              key: 'security_type',
              label: 'Security Type',
              format: 'text'
            },
            {
              key: 'security_code_description',
              label: 'Security Description',
              format: 'text'
            },
            {
              key: 'worst_status',
              label: 'Worst Status',
              format: 'text'
            },
            {
              key: 'worst_status_date',
              label: 'Worst Status Date',
              format: 'date'
            },
            {
              key: 'notes',
              label: 'Notes',
              format: 'text'
            }
          ],
          data: reportData.credit_facility_details.closed
        }] : []),
        
        // Credit Summary
        {
          title: 'Credit Summary',
          icon: 'las la-chart-pie',
          type: 'summary-grid',
          cards: [
            {
              key: 'credit_summary.total_exposure',
              label: 'Total Exposure',
              format: 'currency'
            },
            {
              key: 'credit_summary.total_outstanding',
              label: 'Total Outstanding',
              format: 'currency'
            },
            {
              key: 'credit_summary.total_overdue',
              label: 'Total Overdue',
              format: 'currency',
              highlight: true
            },
            {
              key: 'credit_summary.credit_utilization_rate_on_credit_cards',
              label: 'Credit Utilization Rate',
              format: 'percentage'
            },
            {
              key: 'credit_summary.oldest_active_contract_start_date',
              label: 'Oldest Active Contract Start Date',
              format: 'date'
            },
            {
              key: 'credit_summary.newest_contract_start_date',
              label: 'Newest Active Contract Start Date',
              format: 'date'
            }
          ]
        },
        
        // Credit Applications
        ...(reportData.credit_applications && reportData.credit_applications.length > 0 ? [{
          title: 'Credit Applications',
          icon: 'las la-file-invoice',
          type: 'credit-applications',
          fields: [
            {
              key: 'dp_application_no',
              label: 'Application No',
              format: 'text'
            },
            {
              key: 'provider',
              label: 'Provider',
              format: 'text'
            },
            {
              key: 'type_of_contract',
              label: 'Contract Type',
              format: 'text'
            },
            {
              key: 'phase',
              label: 'Phase',
              format: 'text'
            },
            {
              key: 'role',
              label: 'Role',
              format: 'text'
            },
            {
              key: 'total_amount',
              label: 'Total Amount',
              format: 'currency'
            },
            {
              key: 'no_of_instalments',
              label: 'No of Instalments',
              format: 'text'
            },
            {
              key: 'enquiry_date',
              label: 'Enquiry Date',
              format: 'date'
            },
            {
              key: 'date_of_last_update',
              label: 'Last Update Date',
              format: 'date'
            },
            {
              key: 'notes',
              label: 'Notes',
              format: 'text'
            }
          ],
          data: reportData.credit_applications
        }] : []),
        
      ];
    }
  }
}
</script>
